import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Plus, 
  Search, 
  Filter, 
  Trash2, 
  Edit3, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Target, 
  Volume2, 
  Mail, 
  Smartphone, 
  Settings,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { useAppStore } from '../store';
import { cn } from '../utils';

interface Alert {
  id: string;
  symbol: string;
  name: string;
  type: 'price' | 'volume' | 'change' | 'technical';
  condition: 'above' | 'below' | 'equals';
  targetValue: number;
  currentValue: number;
  status: 'active' | 'triggered' | 'paused';
  createdAt: Date;
  triggeredAt?: Date;
  notifications: ('email' | 'push' | 'sms')[];
  description: string;
}

const Alerts: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock alerts data - Solo indici e futures specificati
  const [alerts, setAlerts] = useState<Alert[]>([
    {
      id: '1',
      symbol: 'GOLD',
      name: 'Gold Futures',
      type: 'price',
      condition: 'above',
      targetValue: 2100,
      currentValue: 2085,
      status: 'active',
      createdAt: new Date('2024-01-15'),
      notifications: ['email', 'push'],
      description: 'Gold price above $2,100/oz'
    },
    {
      id: '2',
      symbol: 'BTCUSD',
      name: 'Bitcoin Futures',
      type: 'change',
      condition: 'above',
      targetValue: 5.0,
      currentValue: 3.2,
      status: 'active',
      createdAt: new Date('2024-01-14'),
      notifications: ['push'],
      description: 'Daily change above 5.0%'
    },
    {
      id: '3',
      symbol: 'SPX',
      name: 'S&P 500 Index',
      type: 'price',
      condition: 'above',
      targetValue: 5000,
      currentValue: 4985,
      status: 'triggered',
      createdAt: new Date('2024-01-13'),
      triggeredAt: new Date('2024-01-16'),
      notifications: ['email', 'push', 'sms'],
      description: 'S&P 500 above 5,000 points'
    },
    {
      id: '4',
      symbol: 'NDX',
      name: 'NASDAQ 100',
      type: 'technical',
      condition: 'above',
      targetValue: 70,
      currentValue: 65,
      status: 'active',
      createdAt: new Date('2024-01-12'),
      notifications: ['email'],
      description: 'RSI above 70 (overbought)'
    },
    {
      id: '5',
      symbol: 'DAX',
      name: 'DAX Index',
      type: 'price',
      condition: 'below',
      targetValue: 17000,
      currentValue: 17250,
      status: 'active',
      createdAt: new Date('2024-01-11'),
      notifications: ['push'],
      description: 'DAX below 17,000 points'
    },
    {
      id: '6',
      symbol: 'FTSE',
      name: 'FTSE 100',
      type: 'change',
      condition: 'below',
      targetValue: -1.0,
      currentValue: -0.3,
      status: 'active',
      createdAt: new Date('2024-01-10'),
      notifications: ['email', 'push'],
      description: 'Daily change below -1.0%'
    },
    {
      id: '7',
      symbol: 'N225',
      name: 'Nikkei 225',
      type: 'price',
      condition: 'above',
      targetValue: 40000,
      currentValue: 39850,
      status: 'paused',
      createdAt: new Date('2024-01-09'),
      notifications: ['push'],
      description: 'Nikkei 225 above 40,000 points'
    },
    {
      id: '8',
      symbol: 'DXY',
      name: 'US Dollar Index',
      type: 'technical',
      condition: 'below',
      targetValue: 30,
      currentValue: 35,
      status: 'triggered',
      createdAt: new Date('2024-01-08'),
      triggeredAt: new Date('2024-01-15'),
      notifications: ['email'],
      description: 'DXY RSI below 30 (oversold)'
    }
  ]);

  const alertTypes = ['all', 'price', 'volume', 'change', 'technical'];
  const alertStatuses = ['all', 'active', 'triggered', 'paused'];

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || alert.type === selectedType;
    const matchesStatus = selectedStatus === 'all' || alert.status === selectedStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'price': return Target;
      case 'volume': return Volume2;
      case 'change': return TrendingUp;
      case 'technical': return BarChart3;
      default: return Bell;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'triggered': return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'paused': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  const getConditionIcon = (condition: string) => {
    switch (condition) {
      case 'above': return TrendingUp;
      case 'below': return TrendingDown;
      case 'equals': return Target;
      default: return Target;
    }
  };

  const getProgressPercentage = (current: number, target: number, condition: string) => {
    if (condition === 'above') {
      return Math.min((current / target) * 100, 100);
    } else if (condition === 'below') {
      return Math.min(((target - current) / target) * 100, 100);
    }
    return 50;
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setLastUpdate(new Date());
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleDeleteAlert = (id: string) => {
    setAlerts(alerts.filter(alert => alert.id !== id));
  };

  const handleToggleStatus = (id: string) => {
    setAlerts(alerts.map(alert => {
      if (alert.id === id) {
        const newStatus = alert.status === 'active' ? 'paused' : 'active';
        return { ...alert, status: newStatus };
      }
      return alert;
    }));
  };

  const formatValue = (value: number, type: string) => {
    if (type === 'change') return `${value}%`;
    if (type === 'volume') return value.toLocaleString();
    return value.toFixed(4);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Price Alerts</h1>
          <p className="text-gray-400">Monitor your investments with custom alerts and notifications</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              "flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all duration-200",
              isRefreshing && "opacity-50 cursor-not-allowed"
            )}
          >
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
            Refresh
          </button>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-all duration-200"
          >
            <Plus className="w-4 h-4" />
            Create Alert
          </button>
        </div>
      </div>

      {/* Alert Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-400">Total Alerts</span>
          </div>
          <p className="text-xl font-bold text-white">{alerts.length}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm text-gray-400">Active</span>
          </div>
          <p className="text-xl font-bold text-green-400">
            {alerts.filter(alert => alert.status === 'active').length}
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-gray-400">Triggered</span>
          </div>
          <p className="text-xl font-bold text-red-400">
            {alerts.filter(alert => alert.status === 'triggered').length}
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-gray-400">Paused</span>
          </div>
          <p className="text-xl font-bold text-yellow-400">
            {alerts.filter(alert => alert.status === 'paused').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {alertTypes.map(type => (
              <option key={type} value={type} className="bg-slate-800">
                {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
          
          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {alertStatuses.map(status => (
              <option key={status} value={status} className="bg-slate-800">
                {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map((alert) => {
          const TypeIcon = getTypeIcon(alert.type);
          const ConditionIcon = getConditionIcon(alert.condition);
          const progress = getProgressPercentage(alert.currentValue, alert.targetValue, alert.condition);
          
          return (
            <div key={alert.id} className="glass-card p-6 hover:bg-white/5 transition-all duration-200">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Alert Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <TypeIcon className="w-5 h-5 text-blue-400" />
                    <div>
                      <h3 className="text-lg font-bold text-white">{alert.symbol}</h3>
                      <p className="text-sm text-gray-400">{alert.name}</p>
                    </div>
                    
                    <div className={cn(
                      "px-2 py-1 rounded border text-xs font-medium ml-auto",
                      getStatusColor(alert.status)
                    )}>
                      {alert.status}
                    </div>
                  </div>
                  
                  <p className="text-gray-300 text-sm mb-3">{alert.description}</p>
                  
                  {/* Alert Condition */}
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <ConditionIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-400">
                        {alert.condition} {formatValue(alert.targetValue, alert.type)}
                      </span>
                    </div>
                    
                    <div className="text-sm">
                      <span className="text-gray-400">Current: </span>
                      <span className="text-white font-medium">
                        {formatValue(alert.currentValue, alert.type)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{progress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Notifications */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Notifications:</span>
                    {alert.notifications.includes('email') && <Mail className="w-3 h-3 text-blue-400" />}
                    {alert.notifications.includes('push') && <Smartphone className="w-3 h-3 text-green-400" />}
                    {alert.notifications.includes('sms') && <Volume2 className="w-3 h-3 text-purple-400" />}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleStatus(alert.id)}
                    className={cn(
                      "px-3 py-1 rounded text-xs font-medium transition-colors",
                      alert.status === 'active' 
                        ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                        : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    )}
                  >
                    {alert.status === 'active' ? 'Pause' : 'Activate'}
                  </button>
                  
                  <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                    <Edit3 className="w-4 h-4 text-gray-400 hover:text-blue-400" />
                  </button>
                  
                  <button 
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                  </button>
                </div>
              </div>
              
              {alert.triggeredAt && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-gray-400">
                    Triggered on {alert.triggeredAt.toLocaleDateString()} at {alert.triggeredAt.toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {filteredAlerts.length === 0 && (
        <div className="glass-card p-12 text-center">
          <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No alerts found</h3>
          <p className="text-gray-400">Create your first alert or adjust your search criteria</p>
        </div>
      )}
      
      {/* Create Alert Modal Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="glass-card p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Create New Alert</h3>
            <p className="text-gray-400 mb-4">Alert creation form will be implemented here</p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;